import { useMemo, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Icon } from '../ui/icon';

export interface WirkungsbalkenZeile {
  id: string;
  name: string;
  /** Wirkung als Zahl, bestimmt Seite und Länge: negativ nach links, positiv nach rechts. */
  wert: number;
  /** Kurzform rechts im Kopf: „−2 Monate“, „ohne Wirkung“. */
  wirkung: ReactNode;
  /**
   * `false`, wenn die Zeile groß ist und trotzdem nichts bewegt (Umsatz, der
   * erst nach dem Engpass eingeht): Die Kurzform bleibt dann ohne Farbe, sonst
   * liest man sie als Gewinn oder Verlust.
   */
  bewegt?: boolean;
  /** Unter dem Balken links, etwa der Gesamtbetrag. */
  betrag?: ReactNode;
  /** Unter dem Balken rechts, etwa „Tiefpunkt −12.000 €“. */
  detail?: ReactNode;
  /** Satz vor den Aktionen: „reicht dann bis Mai 2027“. */
  folge?: ReactNode;
  /** Knöpfe der Zeile. Immer sichtbar, auch am Handy, wo es kein Hover gibt. */
  aktionen?: ReactNode;
}

export interface WirkungsbalkenProps {
  /** In der gewünschten Reihenfolge, meist nach Wirkung sortiert. */
  zeilen: WirkungsbalkenZeile[];
  /** IDs der gewählten Zeilen. */
  ausgewaehlt: ReadonlySet<string>;
  onUmschalten: (id: string) => void;
  /** Beschriftung über der linken und der rechten Seite: „kostet“, „bringt“. */
  links: string;
  rechts: string;
  /** Steht da, wenn es keine Zeilen gibt. */
  leer?: ReactNode;
  /** Satz unter der Liste, etwa dass die Werte nicht addierbar sind. */
  fuss?: ReactNode;
  className?: string;
}

/**
 * Mehrere Optionen nebeneinander, divergierend um eine Nulllinie: was kostet,
 * nach links in `danger`, was bringt, nach rechts in `success`. Jede Zeile ist
 * auswählbar; gewählte Zeilen tragen `chart-2`, die Farbe der Szenario-Linie im
 * Diagramm daneben, damit beides sichtbar zusammengehört.
 */
export function Wirkungsbalken({ zeilen, ausgewaehlt, onUmschalten, links, rechts, leer, fuss, className }: WirkungsbalkenProps) {
  const groesste = useMemo(() => Math.max(1, ...zeilen.map((z) => Math.abs(z.wert))), [zeilen]);

  if (zeilen.length === 0) {
    return leer ? <p className={cn('py-6 text-center t-body text-muted-foreground', className)}>{leer}</p> : null;
  }

  return (
    <div className={className}>
      <div className="mb-2 flex items-center justify-between t-small text-muted-foreground" aria-hidden>
        <span>{links}</span>
        <span>{rechts}</span>
      </div>

      <ul className="flex flex-col gap-1">
        {zeilen.map((z) => {
          const aktiv = ausgewaehlt.has(z.id);
          const abfluss = z.wert < 0;
          const anteil = (Math.abs(z.wert) / groesste) * 50;
          const ton = z.bewegt === false ? 'text-muted-foreground' : abfluss ? 'text-danger' : 'text-success';
          return (
            <li
              key={z.id}
              className={cn(
                'rounded-lg border transition-colors duration-150',
                aktiv ? 'border-chart-2 bg-chart-2/10' : 'border-transparent hover:bg-accent',
              )}
            >
              <button
                type="button"
                aria-pressed={aktiv}
                onClick={() => onUmschalten(z.id)}
                className="w-full rounded-lg px-3 pb-1.5 pt-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="flex items-baseline justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      aria-hidden
                      className={cn(
                        'flex size-4 shrink-0 items-center justify-center self-center rounded-xs border',
                        aktiv ? 'border-chart-2 bg-chart-2 text-card' : 'border-input bg-card',
                      )}
                    >
                      {aktiv && <Icon icon={Check} size={12} strokeWidth={3} />}
                    </span>
                    <span className="truncate t-body text-foreground">{z.name}</span>
                  </span>
                  <span className={cn('shrink-0 t-small font-medium tabular', ton)}>{z.wirkung}</span>
                </span>

                <span className="relative mt-2 block h-2.5" aria-hidden>
                  <span className="absolute inset-0 rounded-xs bg-muted" />
                  <span className="absolute inset-y-0 left-1/2 w-px bg-muted-foreground" />
                  <span
                    className={cn('absolute inset-y-0 rounded-xs', abfluss ? 'bg-danger' : 'bg-success')}
                    style={abfluss ? { right: '50%', width: `${anteil}%` } : { left: '50%', width: `${anteil}%` }}
                  />
                </span>

                {(z.betrag || z.detail) && (
                  <span className="mt-1 flex items-baseline justify-between gap-3 t-small text-muted-foreground">
                    <span className="tabular">{z.betrag}</span>
                    <span className="tabular">{z.detail}</span>
                  </span>
                )}
              </button>

              {(z.folge || z.aktionen) && (
                <div className="flex flex-wrap items-center justify-end gap-2 px-3 pb-2">
                  {z.folge && <span className="mr-auto t-small text-muted-foreground">{z.folge}</span>}
                  {z.aktionen}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {fuss && <p className="mt-3 border-t border-border pt-2 t-small text-muted-foreground">{fuss}</p>}
    </div>
  );
}
